/**
 * Historical-fee upload client (fee decoupling B-hist, 2026-07-11).
 *
 * Backend router: app/routers/agreement_fee_history.py, prefix /agreement-fee-history.
 *   - GET  /template   blank 1-sheet "Fees ex-VAT" .xlsx (Admin)
 *   - POST /import      two-phase bulk import, mode=preview|commit (Admin)
 *
 * The historical fee decoupled from the bundled AuM workbook into its own store +
 * upload; same CIIF-based `MetricsImportReport` shape as the bulk metrics import, so the
 * report type is reused. Multipart + template download use a raw fetch (mirrors
 * monthlyMetrics.ts). No blocking_errors here — a historical fee may be a negative credit note.
 */
import { downloadBlob } from './iaaExports';
import type { MetricsImportConflict, MetricsImportMode, MetricsImportReport } from './monthlyMetrics';

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

function fileNameFromCD(cd: string, fallback: string): string {
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

export const feeHistoryImportApi = {
  /** Bulk import historical fees. preview parses + reconciles and writes nothing; commit
   *  applies (on_conflict 'overwrite' replaces existing months, 'skip' leaves them). The
   *  backend caches nothing — the caller keeps the same File and re-sends it on commit. */
  async importFile(
    file: File,
    mode: MetricsImportMode,
    onConflict: MetricsImportConflict,
  ): Promise<MetricsImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    const res = await fetch('/agreement-fee-history/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The fee-history file could not be processed.'));
    }
    return res.json();
  },

  /** Download the blank, CIIF-columned fee-history template (.xlsx). */
  async downloadTemplate(): Promise<void> {
    const res = await fetch('/agreement-fee-history/template', {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The template could not be generated.'));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'fee_history_template.xlsx'));
  },
};
